import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const AccessControlPanel = ({ document, onUpdate }) => {
  const { user } = useAuth();
  const [accessList, setAccessList] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);

  useEffect(() => {
    if (document?.id) {
      loadAccessData();
    }
  }, [document?.id]);

  const loadAccessData = async () => {
    try {
      setLoading(true);
      setError('');

      const [accessResult, activityResult] = await Promise.all([
        supabase
          ?.from('document_access')
          ?.select(`
            *,
            user_profiles!document_access_user_id_fkey(full_name, email),
            granted_by_profile:user_profiles!document_access_granted_by_fkey(full_name)
          `)
          ?.eq('document_id', document?.id)
          ?.order('granted_at', { ascending: false }),
        supabase
          ?.from('document_activity_log')
          ?.select(`
            *,
            user_profiles!document_activity_log_user_id_fkey(full_name, email)
          `)
          ?.eq('document_id', document?.id)
          ?.order('created_at', { ascending: false })
          ?.limit(20)
      ]);

      if (accessResult?.error) throw accessResult?.error;
      if (activityResult?.error) throw activityResult?.error;

      setAccessList(accessResult?.data || []);
      setActivityLog(activityResult?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load access data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccess = async (accessId, newLevel) => {
    try {
      const { error: updateError } = await supabase
        ?.from('document_access')
        ?.update({ access_level: newLevel })
        ?.eq('id', accessId);

      if (updateError) throw updateError;

      await loadAccessData();
      onUpdate?.();
    } catch (err) {
      setError(err?.message || 'Failed to update access level');
    }
  };

  const handleRevokeAccess = async (accessId) => {
    if (!confirm('Are you sure you want to revoke access for this user?')) return;

    try {
      const { error: deleteError } = await supabase
        ?.from('document_access')
        ?.delete()
        ?.eq('id', accessId);

      if (deleteError) throw deleteError;

      await loadAccessData();
      onUpdate?.();
    } catch (err) {
      setError(err?.message || 'Failed to revoke access');
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'admin':
        return 'var(--color-destructive)';
      case 'editor':
        return 'var(--color-warning)';
      case 'viewer':
        return 'var(--color-success)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getActionIcon = (action) => {
    const iconMap = {
      INSERT: 'Plus',
      UPDATE: 'Edit',
      DELETE: 'Trash2',
      VIEW: 'Eye',
      DOWNLOAD: 'Download'
    };
    return iconMap?.[action] || 'Activity';
  };

  const accessLevelOptions = [
    { value: 'viewer', label: 'Viewer', description: 'Can view only' },
    { value: 'editor', label: 'Editor', description: 'Can view and edit' },
    { value: 'admin', label: 'Admin', description: 'Full access' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading access control data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Access Control List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
              <Icon name="Shield" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Access Control</h3>
              <p className="text-xs text-muted-foreground">{accessList?.length} users with access</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowAddUser(!showAddUser)}
          >
            Add User
          </Button>
        </div>

        {showAddUser && (
          <div className="mb-4 p-4 bg-background rounded-lg border border-border">
            <p className="text-sm text-muted-foreground mb-3">
              User invitation feature coming soon. You will be able to search and add users with specific access levels.
            </p>
            <Button variant="ghost" size="sm" onClick={() => setShowAddUser(false)}>
              Cancel
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {accessList?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Users" size={48} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">No access records found</p>
            </div>
          ) : (
            accessList?.map(access => (
              <div key={access?.id} className="p-4 bg-background rounded-lg border border-border">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full flex-shrink-0">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium">
                          {access?.user_profiles?.full_name || 'Unknown User'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {access?.user_profiles?.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          options={accessLevelOptions}
                          value={access?.access_level}
                          onChange={(newLevel) => handleUpdateAccess(access?.id, newLevel)}
                          className="w-32"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          iconName="Trash2"
                          onClick={() => handleRevokeAccess(access?.id)}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} />
                        Granted {new Date(access?.granted_at)?.toLocaleDateString()}
                      </span>
                      {access?.granted_by_profile && (
                        <span className="flex items-center gap-1">
                          <Icon name="UserCheck" size={12} />
                          by {access?.granted_by_profile?.full_name}
                        </span>
                      )}
                      {access?.expires_at && (
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          Expires {new Date(access?.expires_at)?.toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Activity Audit Trail */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Icon name="Activity" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Activity Audit Trail</h3>
            <p className="text-xs text-muted-foreground">Recent document activity</p>
          </div>
        </div>

        <div className="space-y-2">
          {activityLog?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Activity" size={48} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">No activity recorded yet</p>
            </div>
          ) : (
            activityLog?.map(log => (
              <div key={log?.id} className="p-3 bg-background rounded-lg border border-border hover:border-primary/20 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-muted rounded flex-shrink-0">
                    <Icon name={getActionIcon(log?.action)} size={12} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">{log?.user_profiles?.full_name || 'System'}</span>
                        {' '}
                        <span className="text-muted-foreground">{log?.action?.toLowerCase()}</span>
                      </p>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {new Date(log?.created_at)?.toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(log?.created_at)?.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessControlPanel;